import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Loader from './Loader';
import './TimeLogs.css';

function TimeLogs() {
  const { currentUser } = useAuth();
  const [timeLogs, setTimeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingLog, setEditingLog] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    date: '',
    hours: '',
    description: ''
  });

  // Get date range (today and 2 days back)
  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 2);
    return date.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Fetch time logs
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'timelogs'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logs = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Handle both old (clockIn/clockOut) and new (hours) format
        let hours = data.hours;
        if (!hours && data.clockIn && data.clockOut) {
          // Convert old format to hours
          const clockIn = data.clockIn.toDate ? data.clockIn.toDate() : new Date(data.clockIn);
          const clockOut = data.clockOut.toDate ? data.clockOut.toDate() : new Date(data.clockOut);
          hours = Math.round((clockOut - clockIn) / (1000 * 60 * 60) * 10) / 10;
        }
        logs.push({
          id: docSnap.id,
          ...data,
          hours: hours || 0
        });
      });
      // Sort by date descending
      logs.sort((a, b) => new Date(b.date) - new Date(a.date));
      setTimeLogs(logs);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching time logs:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateDate = (dateString) => {
    const selectedDate = new Date(dateString);
    const minDate = new Date(getMinDate());
    const maxDate = new Date(getMaxDate());

    selectedDate.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(0, 0, 0, 0);

    return selectedDate >= minDate && selectedDate <= maxDate;
  };

  const resetForm = () => {
    setFormData({ date: '', hours: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date || !formData.hours || !formData.description.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!validateDate(formData.date)) {
      toast.error('You can only submit time logs for today or the last 2 days');
      return;
    }

    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      toast.error('Please enter valid hours (between 0.5 and 24)');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'timelogs'), {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userEmail: currentUser.email,
        date: formData.date,
        hours: hours,
        description: formData.description.trim(),
        status: 'pending',
        createdAt: Timestamp.now()
      });

      resetForm();
      setShowAddModal(false);
      toast.success('Time log submitted successfully!');
    } catch (error) {
      console.error('Error adding time log:', error);
      toast.error('Failed to submit time log. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (log) => {
    // Only allow editing pending entries
    if (log.status !== 'pending') {
      toast.error('You can only edit pending entries');
      return;
    }
    setEditingLog(log);
    setFormData({
      date: log.date,
      hours: log.hours.toString(),
      description: log.description
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!formData.date || !formData.hours || !formData.description.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!validateDate(formData.date)) {
      toast.error('You can only submit time logs for today or the last 2 days');
      return;
    }

    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      toast.error('Please enter valid hours (between 0.5 and 24)');
      return;
    }

    setSubmitting(true);

    try {
      await updateDoc(doc(db, 'timelogs', editingLog.id), {
        date: formData.date,
        hours: hours,
        description: formData.description.trim()
      });

      resetForm();
      setShowEditModal(false);
      setEditingLog(null);
      toast.success('Time log updated successfully!');
    } catch (error) {
      console.error('Error updating time log:', error);
      toast.error('Failed to update time log. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (log) => {
    // Only allow deleting pending entries
    if (log.status !== 'pending') {
      toast.error('You can only delete pending entries');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'timelogs', log.id));
      toast.success('Time log deleted successfully!');
    } catch (error) {
      console.error('Error deleting time log:', error);
      toast.error('Failed to delete time log. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">Approved</span>;
      case 'rejected':
        return <span className="status-badge rejected">Rejected</span>;
      default:
        return <span className="status-badge pending">Pending</span>;
    }
  };

  // Group logs by date
  const groupedLogs = timeLogs.reduce((groups, log) => {
    const date = log.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(log);
    return groups;
  }, {});

  // Calculate total hours for a date
  const getTotalHours = (logs) => {
    return logs.reduce((sum, log) => sum + (log.hours || 0), 0);
  };

  if (loading) {
    return <Loader message="Loading time logs..." />;
  }

  return (
    <div className="timelogs-page">
      {/* Header */}
      <div className="timelogs-header">
        <div>
          <h1>Time Logs</h1>
          <p>Submit and track your daily time entries</p>
        </div>
        <button className="btn-add-log" onClick={() => setShowAddModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Time Log
        </button>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span>You can submit time logs for today and up to 2 days back. Entries need admin approval. You can add multiple entries per day.</span>
      </div>

      {/* Time Logs List */}
      <div className="timelogs-container">
        {Object.keys(groupedLogs).length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <h3>No Time Logs Yet</h3>
            <p>Start by adding your first time log entry</p>
          </div>
        ) : (
          Object.keys(groupedLogs)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((date) => (
              <div key={date} className="date-group">
                <div className="date-header">
                  <span className="date-title">{formatDate(date)}</span>
                  <span className="date-total">Total: {getTotalHours(groupedLogs[date])} hours</span>
                </div>
                <div className="entries-list">
                  {groupedLogs[date].map((log) => (
                    <div key={log.id} className="entry-card">
                      <div className="entry-main">
                        <div className="entry-hours">{log.hours}h</div>
                        <div className="entry-description">{log.description}</div>
                      </div>
                      <div className="entry-actions">
                        {getStatusBadge(log.status)}
                        {log.status === 'pending' && (
                          <div className="entry-buttons">
                            <button className="btn-edit" onClick={() => handleEdit(log)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button className="btn-delete" onClick={() => handleDelete(log)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
        )}
      </div>

      {/* Add Time Log Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Time Log</h3>
              <button className="modal-close" onClick={() => { setShowAddModal(false); resetForm(); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={getMinDate()}
                    max={getMaxDate()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Hours *</label>
                  <input
                    type="number"
                    name="hours"
                    value={formData.hours}
                    onChange={handleInputChange}
                    placeholder="e.g., 2"
                    min="0.5"
                    max="24"
                    step="0.5"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="What did you work on? (e.g., Working on login page)"
                  rows="3"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Time Log Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Time Log</h3>
              <button className="modal-close" onClick={() => { setShowEditModal(false); resetForm(); setEditingLog(null); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={getMinDate()}
                    max={getMaxDate()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Hours *</label>
                  <input
                    type="number"
                    name="hours"
                    value={formData.hours}
                    onChange={handleInputChange}
                    placeholder="e.g., 2"
                    min="0.5"
                    max="24"
                    step="0.5"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="What did you work on? (e.g., Working on login page)"
                  rows="3"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => { setShowEditModal(false); resetForm(); setEditingLog(null); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimeLogs;
